import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import LanguageSwitcher from '../LanguageSwitcher';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  // Navigation links with translations
  const navigationLinks = [
    { id: 1, name: t('about', translations.nav), path: '/about' },
    { id: 2, name: t('services', translations.nav), path: '/services' },
    { id: 3, name: t('news', translations.nav), path: '/news' },
    { id: 4, name: t('partners', translations.nav), path: '/partners' },
    { id: 5, name: t('careers', translations.nav), path: '/careers' },
    { id: 6, name: t('contact', translations.nav), path: '/contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      // Hide navbar when scrolled past 100px
      setIsHidden(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  // Keep transparent on homepage, white on other pages
  const showTransparent = isHomePage;
  const textColor = showTransparent ? 'text-white/80 hover:text-white' : 'text-black/70 hover:text-black';
  const activeTextColor = showTransparent ? 'text-white' : 'text-black';
  const bgStyle = showTransparent 
    ? {} 
    : { background: 'linear-gradient(to bottom, rgba(255,255,255,1) 80%, rgba(255,255,255,0) 100%)' };

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        transform: isHidden ? 'translateY(-100%)' : 'translateY(0)',
        opacity: isHidden ? 0 : 1,
        ...bgStyle,
      }}
      data-testid="navbar"
    >
      <div className="w-full px-4 lg:px-[8%] py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/media/j5tbshc2_Yetkin_logo_new.png"
              alt="Yetkin Dienstleistungen"
              className={`h-[100px] lg:h-[140px] w-auto object-contain ${showTransparent ? 'brightness-0 invert' : ''}`}
            />
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-4">
            {navigationLinks.map((link) => (
              <Link
                key={link.id}
                to={link.path}
                className={`relative px-4 py-2 font-medium capitalize transition-all duration-200 
                  after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-current after:transition-all after:duration-200 hover:after:w-full
                  ${isActive(link.path) ? activeTextColor : textColor}`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Language Switcher - Desktop */}
            <LanguageSwitcher 
              variant={showTransparent ? 'light' : 'default'} 
              className="ml-2"
            />
          </div>

          {/* Mobile Menu Button and Language Switcher */}
          <div className="lg:hidden flex items-center gap-3">
            <LanguageSwitcher 
              variant={showTransparent ? 'light' : 'default'}
            />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 transition-colors ${showTransparent ? 'text-white' : 'text-black'}`}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden absolute top-full left-0 right-0 bg-white border-t border-black/5 transition-all duration-300 overflow-hidden ${isMobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-6 py-4 space-y-1">
          {navigationLinks.map((link) => (
            <Link
              key={link.id}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-3 font-medium capitalize rounded-lg transition-all duration-300 ${
                isActive(link.path) ? 'text-black bg-black/5' : 'text-black/70 hover:text-black hover:bg-black/5'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
